import { Container } from '../../../../components/Container/Container'
import { useMemo } from 'react'
import { CREATE_EDIT_HS_TABS_KEYS, CREATE_HS_BREADCRUMBS } from './constants'
import { useParams } from 'react-router-dom'
import { useHSDetailsContext } from 'features/MyCommunity/HealthSystems/FullView/Providers/HSContextProvider'
import { Menu } from '../../../../components/Menu/Menu'
import useRouter from '../../../../hooks/useRouter'
import { ViewEditHS } from 'features/MyCommunity/HealthSystems/FullView/ViewEditHS'
import 'features/MyCommunity/HealthSystems/FullView/styles.scss'
import { HospitalsTable } from 'features/MyCommunity/HealthSystems/FullView/HospitalsTable/HospitalsTable'
import { Users } from 'features/MyCommunity/HealthSystems/FullView/Users/Users'
import {
  ACTIONS,
  Can,
  SUBJECTS,
  SubjectWithAttributes
} from '../../../Permission'
import DownloadQRCode from '../../../HealthSystemDetails/DownloadQRCode'
import { subject } from '@casl/ability'
import { BankingSetupContainer } from 'features/MyCommunity/BankingSetupCommunity/BankingSetupContainer'
import { HSDetailsBankAccountsContainer } from 'features/MyCommunity/HealthSystems/FullView/HSDetailsBankAccountsContainer'

export const HSDetails = () => {
  const { id } = useParams()
  const router = useRouter()
  const {
    state: {
      details,
      businessAccountDetails,
      activeTab,
      tabs,
      canEditHS,
      canViewHospitals,
      canViewBankingSetup,
      canViewBankAccounts
    },
    actions: { setActiveTab, refreshHSDetails }
  } = useHSDetailsContext()

  const breadcrumbs = useMemo(() => {
    return CREATE_HS_BREADCRUMBS(id, details?.name)
  }, [id, details?.name])

  const title = useMemo(() => {
    return id === 'new' ? 'Health system creation' : details?.name
  }, [id, details?.name])

  const RenderedMenu = useMemo(() => {
    const handleSelectTab = (e) => {
      setActiveTab(e.key)
      router.push(e.key)
    }
    if (id === 'new') return null

    return (
      <Menu
        mode="horizontal"
        onSelect={handleSelectTab}
        selectedKeys={[activeTab]}
        defaultSelectedKeys={[activeTab]}
        items={tabs}
      />
    )
  }, [activeTab, router, setActiveTab, id, tabs])

  const RenderedActions = useMemo(() => {
    return (
      details?.uuid && (
        <div className="row align-center gap-8">
          <Can
            I={ACTIONS.DOWNLOAD}
            a={subject(SUBJECTS.QR_CODE, {
              ...details
            } as unknown as SubjectWithAttributes)}
          >
            <DownloadQRCode uuid={details.uuid} />
          </Can>
        </div>
      )
    )
  }, [details])

  return (
    <Container
      title={title}
      breadcrumbs={breadcrumbs}
      menu={RenderedMenu}
      actions={RenderedActions}
      className="view-edit-hs-container"
    >
      {activeTab === CREATE_EDIT_HS_TABS_KEYS.general && <ViewEditHS />}
      {!activeTab && <ViewEditHS />}
      {activeTab === CREATE_EDIT_HS_TABS_KEYS.users && <Users />}
      {activeTab === CREATE_EDIT_HS_TABS_KEYS.hospitals &&
        (canEditHS || canViewHospitals) && (
          <HospitalsTable hsName={details?.name} canEditHS={canEditHS} />
        )}
      {activeTab === CREATE_EDIT_HS_TABS_KEYS.banking_setup &&
        canViewBankingSetup && (
          <BankingSetupContainer
            communityEntity={{
              entity_id: details?.uuid || '',
              entity_type: 'health_system'
            }}
            businessAccountDetails={businessAccountDetails}
            moovBusinessAccountId={details?.moov_business_account}
            refreshDetails={refreshHSDetails}
          />
        )}
      {activeTab === CREATE_EDIT_HS_TABS_KEYS.bank_accounts &&
        canViewBankAccounts && (
          <HSDetailsBankAccountsContainer
            businessAccountDetails={businessAccountDetails}
            getBusinessAccountDetails={refreshHSDetails}
          />
        )}
    </Container>
  )
}
